import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../../hooks/useConfirm";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { formConfigService } from "../../services/form-config.service";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { FORM_TYPE_LABELS } from "../../interfaces/form-config.interface";
import type { FormPricingRow, AddonConfig } from "../../interfaces/form-config.interface";

const inputClass =
  "w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right";

const FormPricingPage = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.SYSTEM_SETTINGS_UPDATE);

  const [formRows, setFormRows] = useState<FormPricingRow[]>([]);
  const [addonConfig, setAddonConfig] = useState<AddonConfig>({
    boost:     { cost: 20, days: 3 },
    topOfPage: { cost: 20, days: 3 },
    whatsapp:  { cost: 20, days: 90 },
    popupAd:   { cost: 100, days: 30 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingAll, setSavingAll] = useState(false);
  const [savingAddon, setSavingAddon] = useState<string | null>(null);
  const [rowEdits, setRowEdits] = useState<Record<string, { listingCost: number; visibleDays: number }>>({});
  const [addonEdits, setAddonEdits] = useState<AddonConfig | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await formConfigService.getPricing();
      setFormRows(data.formConfigs);
      setAddonConfig(data.addonConfig);
      const edits: Record<string, { listingCost: number; visibleDays: number }> = {};
      data.formConfigs.forEach((r) => { edits[r.id] = { listingCost: r.listingCost, visibleDays: r.visibleDays }; });
      setRowEdits(edits);
      setAddonEdits(data.addonConfig);
    } catch {
      setError(t("failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const saveAllRows = async () => {
    if (!await confirm(t('confirm_save_pricing'))) return;
    setSavingAll(true);
    setError("");
    try {
      const results = await Promise.all(
        formRows.map((row) => formConfigService.updateFormPricing(row.id, rowEdits[row.id])),
      );
      setFormRows((prev) =>
        prev.map((r) => {
          const updated = results.find((u) => u.id === r.id);
          return updated ? { ...r, ...updated } : r;
        }),
      );
    } catch {
      setError(t("failed_to_save"));
    } finally {
      setSavingAll(false);
    }
  };

  const saveAddon = async () => {
    if (!addonEdits) return;
    if (!await confirm(t('confirm_save_addon'))) return;
    setSavingAddon("all");
    try {
      await formConfigService.updateAddonConfig({
        boostCost:     addonEdits.boost.cost,
        boostDays:     addonEdits.boost.days,
        topOfPageCost: addonEdits.topOfPage.cost,
        topOfPageDays: addonEdits.topOfPage.days,
        whatsappCost:  addonEdits.whatsapp.cost,
        whatsappDays:  addonEdits.whatsapp.days,
        popupAdCost:   addonEdits.popupAd.cost,
        popupAdDays:   addonEdits.popupAd.days,
      });
      setAddonConfig(addonEdits);
    } catch {
      setError(t("failed_to_save"));
    } finally {
      setSavingAddon(null);
    }
  };

  const ADDON_ITEMS: Array<{ key: keyof AddonConfig; label: string; desc: string }> = [
    { key: "boost",     label: t("addon_boost"),      desc: t("addon_boost_desc") },
    { key: "topOfPage", label: t("addon_top_of_page"), desc: t("addon_top_of_page_desc") },
    { key: "whatsapp",  label: t("addon_whatsapp"),   desc: t("addon_whatsapp_desc") },
    { key: "popupAd",   label: t("addon_popup_ad"),   desc: t("addon_popup_ad_desc") },
  ];

  return (
    <Layout title={t("form_pricing_title")}>
      <div className="space-y-8">
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* ── Form Config Pricing Table ── */}
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t("form_pricing_table_title")}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t("form_pricing_table_desc")}</p>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-400">{t("loading")}</div>
          ) : (
            <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="px-4 py-2">{t("form_type")}</th>
                  <th className="px-4 py-2 text-right">{t("listing_cost_pts")}</th>
                  <th className="px-4 py-2 text-right">{t("visible_days")}</th>
                </tr>
              </thead>
              <tbody>
                {formRows.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">
                      {FORM_TYPE_LABELS[row.formType]}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canEdit ? (
                        <input
                          type="number"
                          min={0}
                          className={inputClass}
                          value={rowEdits[row.id]?.listingCost ?? row.listingCost}
                          onChange={(e) => setRowEdits((prev) => ({ ...prev, [row.id]: { ...prev[row.id], listingCost: parseInt(e.target.value, 10) || 0 } }))}
                        />
                      ) : (
                        <span>{row.listingCost} pts</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {canEdit ? (
                        <input
                          type="number"
                          min={1}
                          className={inputClass}
                          value={rowEdits[row.id]?.visibleDays ?? row.visibleDays}
                          onChange={(e) => setRowEdits((prev) => ({ ...prev, [row.id]: { ...prev[row.id], visibleDays: parseInt(e.target.value, 10) || 1 } }))}
                        />
                      ) : (
                        <span>{row.visibleDays}d</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canEdit && (
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                <Button variant="primary" size="sm" onClick={saveAllRows} disabled={savingAll}>
                  {savingAll ? t("loading") : t("save_all_pricing")}
                </Button>
              </div>
            )}
            </>
          )}
        </Card>

        {/* ── Global Add-on Settings ── */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t("addon_settings_title")}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{t("addon_settings_desc")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {ADDON_ITEMS.map(({ key, label, desc }) => (
              <Card key={key} className="p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t("cost_pts")}</span>
                    {canEdit ? (
                      <input
                        type="number"
                        min={0}
                        className={inputClass}
                        value={addonEdits?.[key].cost ?? addonConfig[key].cost}
                        onChange={(e) => setAddonEdits((prev) => prev ? { ...prev, [key]: { ...prev[key], cost: parseInt(e.target.value, 10) || 0 } } : null)}
                      />
                    ) : (
                      <span className="text-sm font-medium">{addonConfig[key].cost} pts</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{t("duration_days")}</span>
                    {canEdit ? (
                      <input
                        type="number"
                        min={1}
                        className={inputClass}
                        value={addonEdits?.[key].days ?? addonConfig[key].days}
                        onChange={(e) => setAddonEdits((prev) => prev ? { ...prev, [key]: { ...prev[key], days: parseInt(e.target.value, 10) || 1 } } : null)}
                      />
                    ) : (
                      <span className="text-sm font-medium">{addonConfig[key].days}d</span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {canEdit && (
            <div className="mt-4 flex justify-end">
              <Button variant="primary" size="sm" onClick={saveAddon} disabled={savingAddon === "all"}>
                {savingAddon === "all" ? t("loading") : t("save_addon_settings")}
              </Button>
            </div>
          )}
        </div>
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default FormPricingPage;
